import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Report templates — admin-curated column layouts for society exports
 * (РАО / ВОИС / РСП). PRD §12.2 + §13.8; decisions §34;
 * admin-arch §3 lines 287-297. Wave 2.42 BE-2 (TASK-094e).
 *
 * Five schemas:
 *   - `reportTemplateRowSchema`         — single-row wire DTO
 *   - `reportTemplateListResponseSchema` — paginated list response
 *   - `reportTemplateCreateSchema`      — POST body
 *   - `reportTemplatePatchSchema`       — PATCH body (code immutable)
 *   - `reportTemplateListQuerySchema`   — GET query params (search, filter)
 */
/**
 * Whitelist of column `field` values accepted in `columns[].field`.
 * Mirrors `REPORT_TEMPLATE_FIELDS` in `apps/api/src/db/schema/internal/
 * report_templates.ts`. Decisions §34 — extending requires a code
 * migration so SQL injection through template payloads stays impossible.
 */
export const reportTemplateFieldSchema = z.enum([
    'artist',
    'title',
    'isrc',
    'duration_seconds',
    'plays',
    'venue_name',
    'period_start',
    'period_end',
    'release_year',
]);
export const reportTemplateSocietySchema = z.enum([
    'rao',
    'vois',
    'rsp',
    'other',
]);
export const reportTemplateFormatSchema = z.enum(['csv', 'xlsx']);
/**
 * One column inside `columns`. `order` is 1-based and forms a strictly
 * increasing sequence across the array (validated at the array level).
 * `header` is the human-facing label rendered into the CSV/XLSX header row.
 */
export const reportTemplateColumnSchema = z
    .object({
    field: reportTemplateFieldSchema,
    header: z.string().trim().min(1).max(255),
    order: z.number().int().min(1).max(64),
})
    .strict();
/**
 * `columns` array. Backend additionally checks that:
 *   - at least one column is present;
 *   - `field` values are unique (a column appears at most once);
 *   - `order` values are unique (no duplicate slots).
 * The exporter consumes the array sorted by `order` ascending.
 */
export const reportTemplateColumnsSchema = z
    .array(reportTemplateColumnSchema)
    .min(1, 'At least one column is required')
    .max(64, 'A template may contain at most 64 columns')
    .superRefine((cols, ctx) => {
    const seenFields = new Set();
    const seenOrders = new Set();
    for (const col of cols) {
        if (seenFields.has(col.field)) {
            ctx.addIssue({
                code: 'custom',
                path: [],
                message: `Duplicate column field: ${col.field}`,
            });
            return;
        }
        seenFields.add(col.field);
        if (seenOrders.has(col.order)) {
            ctx.addIssue({
                code: 'custom',
                path: [],
                message: `Duplicate column order: ${col.order}`,
            });
            return;
        }
        seenOrders.add(col.order);
    }
});
/**
 * Wire DTO for a single template row.
 */
export const reportTemplateRowSchema = z
    .object({
    id: uuidSchema,
    code: z.string().min(2).max(64),
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    society: reportTemplateSocietySchema,
    format: reportTemplateFormatSchema,
    columns: reportTemplateColumnsSchema,
    is_active: z.boolean(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
})
    .strict();
/**
 * List response — offset/limit pagination with `total` for a page counter
 * (FE renders the settings screen as a finite grid; cursor is overkill
 * for a small curated catalog).
 */
export const reportTemplateListResponseSchema = z
    .object({
    items: z.array(reportTemplateRowSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
})
    .strict();
export const reportTemplateCreateSchema = z
    .object({
    code: z
        .string()
        .trim()
        .min(2)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case starting with a letter'),
    name: z.string().trim().min(1).max(255),
    description: z.string().trim().max(2000).optional(),
    society: reportTemplateSocietySchema,
    format: reportTemplateFormatSchema,
    columns: reportTemplateColumnsSchema,
    is_active: z.boolean().optional(),
})
    .strict();
/**
 * PATCH body. `code` is intentionally absent — codes are immutable
 * (PRD §13.8: client-side bookmarks / export dropdown selection must
 * survive curator edits).
 */
export const reportTemplatePatchSchema = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    society: reportTemplateSocietySchema.optional(),
    format: reportTemplateFormatSchema.optional(),
    columns: reportTemplateColumnsSchema.optional(),
    is_active: z.boolean().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
/**
 * GET list query — search by code/name (case-insensitive substring),
 * filter by society and `is_active`, simple offset pagination.
 *
 * Offset (not cursor) is chosen because the dataset is small (curated
 * by hand, dozens of rows at most) and FE wants a page counter for the
 * "Шаблоны отчётов" settings screen.
 */
export const reportTemplateListQuerySchema = z
    .object({
    q: z.string().trim().min(1).max(255).optional(),
    society: reportTemplateSocietySchema.optional(),
    is_active: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
})
    .strict();
//# sourceMappingURL=report-template.js.map